import React, { useState } from 'react';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  numberWithCommas,
} from 'common/formatters';

import {
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';

import {
  getGoingToMarketCheckList,
} from 'common/marketingPartnerListingHelpers';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrSeparator from 'client/components/SrSeparator';
import SrButton, {
  SR_BUTTON_HEIGHT_MEDIUM,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrStepsChecklist, {
  SR_STEPS_CHECKLIST_STATUS_NOT_STARTED,
  SR_STEPS_CHECKLIST_STATUS_COMPLETED,
} from 'client/components/SrStepsChecklist/SrStepsChecklist';
import SrPreviewAndDownloadDocsModal from 'client/components/SrPreviewAndDownloadDocsModal';
import SrTag, {
  SR_TAG_THEME_LIGHTBLUE_BLACK,
  SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER,
  SR_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrTag/SrTag';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrMarkInstallationAsCompletedModal from 'client/components/SrMarkInstallationAsCompletedModal';
import SrModalV2, {
  MODAL_THEME_STACK,
} from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import ListingActions from 'client/actions/Listing';

const getFormFields = () => [
  {
    id: 'marketingItemsEmailSentToPartner',
    type: 'yes_no',
    validations: ['required'],
    className: 'yesnoFullWidth',
  },
];
const SrEmailSentToPartnerModal = ({
  onClose,
  onSubmit,
}) => {
  return (
    <SrModalV2
      closeOnBackgroundClick
      onClose={onClose}
      showCloseButton={false}
      contentMaxWidth='375px'
      contentPadding='64px 16px 32px'
      theme={MODAL_THEME_STACK}
      contentHeight='598px'
    >
      <SrFlex direction='col' height='100%'>
        <SrFlex justify='center'>
          <SrText
            color='blueGreycliff'
            font='greycliff'
            fontSize='26px'
            lineHeight='33px'
            weight='800'
            align='center'
            block='block'
          >
            Has this listing been submitted to the partner?
          </SrText>
        </SrFlex>
        <Formik
          initialValues={createInitialValues({
            formFields: getFormFields(),
            defaultValues: {
              marketingItemsEmailSentToPartner: null,
            },
          })}
          onSubmit={onSubmit}
          validate={(formValues) => {
            const formFields = getFormFields({ values: formValues });
            return createFormValidationFn(formFields)(formValues);
          }}
          render={(formikBag) => {
            const formFields = getFormFields({
              values: formikBag.values,
            });

            return (
              <SrFlex width='100%' height='calc(100% - 66px)'>
                <Form style={{ width: '100%', height: '100%' }}>
                  <SrFlex width='100%' height='100%' direction='col'>
                    {formFields.map((formField) => (
                      <SrBox key={formField.id} margin='10px 0'>
                        <SrFormikField
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                          className={formField.className}
                        />
                      </SrBox>
                    ))}
                    <SrFlex marginTop='auto'>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        type='submit'
                        disabled={!formikBag.isValid}
                      >
                        Save
                      </SrButton>
                    </SrFlex>
                  </SrFlex>
                </Form>
              </SrFlex>
            );
          }}
        />
      </SrFlex>
    </SrModalV2>
  );
};

const SrGoingToMarketSunRiseListingCard = ({
  isAdmin,
  reloadData,
  isHighlighted,
  marketingListing,
  onEditWalkThroughFields,
  onEditMarketingPartnerListingDueDate,
}) => {
  const dispatch = useDispatch();
  const {
    appUrl,
    currentUser,
  } = useBranch({
    appUrl: ['env', 'APP_URL'],
    currentUser: ['currentUser'],
  });
  const [downloadDocModalOpen, setDownloadDocModalOpen] = useState(false);
  const [collapseCard, setCollapseCard] = useState(isAdmin);
  const [listingNotesModal, setListingNotesModal] = useState(null);
  const [showEmailSentToPartnerModal, setShowEmailSentToPartnerModal] = useState(false);
  const [showMarkInstallationAsCompletedModal, setShowMarkInstallationAsCompletedModal] = useState(false);

  if (!marketingListing) return null;

  const SrCardOrSrBox = isAdmin ? SrLink : SrBox;
  const getStatus = (status) => {
    return status
      ? SR_STEPS_CHECKLIST_STATUS_COMPLETED
      : SR_STEPS_CHECKLIST_STATUS_NOT_STARTED;
  };
  const onDownloadAdditionalDocs = () => {
    const stringQuery = JSON.stringify({
      listingId: marketingListing.listing.id,
    });
    const encodedQuery = encodeURIComponent(stringQuery);
    const downloadDocumentsUrl = `${appUrl}/bundle-files/additional-marketing-docs?data=${encodedQuery}`;
    global.open(downloadDocumentsUrl);
  };
  const hasDocsForDownload = marketingListing.additionalMarketingDocs?.length > 0;
  const submittedAt = momentWithAustinTimezone(marketingListing.createdDate)
    .format('MMM D [at] h:mma');
  const onSaveSentToPartner = async (formValues) => {
    await dispatch(ListingActions.updateGoingToMarketEmailSentToPartner, {
      marketingListingId: marketingListing.id,
      ...formValues,
    });

    if (reloadData) {
      reloadData(marketingListing);
    }

    setShowEmailSentToPartnerModal(false);
  };
  const onMarkInstallationAsCompleted = async (formValues) => {
    await dispatch(ListingActions.markInstallationAsCompleted, {
      marketingListingId: marketingListing.id,
      ...formValues,
    });

    if (reloadData) {
      reloadData(marketingListing);
    }

    setShowMarkInstallationAsCompletedModal(false);
  };

  const {
    price,
    CMACompleted,
    BPOCompleted,
    shouldRunCMA,
    photosCompleted,
    marketingItemsCompleted,
    shouldHideMarketingItems,
    shouldCollectInfoFromOwner,
    marketingInfoRequiredCompleted,
    descriptionCompleted,
    shouldHidePhotos,
    shouldHidePropertyConditionReport,
    propertyConditionReportCompleted,
    emailSentToPartnerCompleted,
  } = getGoingToMarketCheckList(marketingListing);

  const itemsNotInstalledContent = (items) => {
    return (
      <SrBox
        marginLeft='30px'
      >
        <SrText
          color='orange300'
          font='greycliff'
          size='14'
        >
          Items not installed: {items.join(', ')}
        </SrText>
      </SrBox>
    );
  };

  const checkListSteps = lodashCompact([
    shouldRunCMA && {
      label: 'Create CMA',
      status: getStatus(CMACompleted),
    },
    {
      label: 'Write marketing description',
      status: getStatus(descriptionCompleted),
    },
    shouldRunCMA && {
      label: 'Generate BPO',
      status: getStatus(BPOCompleted),
    },
    shouldRunCMA && {
      label: `Owner approved price ${price != null ? `($${numberWithCommas(price)})` : ''}`,
      status: getStatus(price != null),
    },
    shouldCollectInfoFromOwner && {
      label: 'Extra marketing info from owner',
      status: getStatus(marketingInfoRequiredCompleted),
      content: marketingListing.marketingInfoRequiredFromOwner,
      contentLeftPadding: true,
    },
    !shouldHideMarketingItems && {
      label: 'Marketing items install',
      status: getStatus(marketingItemsCompleted),
      ctaLabel: !marketingItemsCompleted && 'Edit',
      onCtaClick: !marketingItemsCompleted && (() => setShowMarkInstallationAsCompletedModal(true)),
      content: marketingListing?.markedInstallMissingItems
        && marketingListing?.markedInstallMissingItems.length > 0
        ? itemsNotInstalledContent(marketingListing.markedInstallMissingItems)
        : null,
    },
    !shouldHidePropertyConditionReport && {
      label: 'Property Condition Report',
      status: getStatus(propertyConditionReportCompleted),
    },
    !shouldHidePhotos && {
      label: 'Professional photos',
      status: getStatus(photosCompleted),
    },
    // This one should always be the last check
    isAdmin && {
      label: 'Sent to Partner',
      ctaLabel: !emailSentToPartnerCompleted && 'Edit',
      onCtaClick: !emailSentToPartnerCompleted && (() => setShowEmailSentToPartnerModal(true)),
      status: getStatus(emailSentToPartnerCompleted),
    },
  ]);
  const stateText = [
    `${marketingListing.listing.addressCity},`,
    marketingListing.listing.addressStateCode,
    marketingListing.listing.addressZip,
  ].join(' ');

  const formatPartnerListingDueDate = (date) => {
    if (!date) {
      return '';
    }
    return momentWithAustinTimezone(date).format('MMM D');
  };
  const onDownloadDocsClick = () => {
    if (!hasDocsForDownload) {
      return null;
    }

    setDownloadDocModalOpen(true);
  };
  const onNoteClick = (event, listing) => {
    event.preventDefault();

    setListingNotesModal(listing);
  };
  const getNotesTagComponent = () => {
    const hasNotes = marketingListing.notesCount > 0;
    const blueTheme = isAdmin ? SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER : SR_TAG_THEME_LIGHTBLUE_BLACK;
    const theme = hasNotes
      ? blueTheme
      : SR_TAG_THEME_WHITE_BLACK;
    const onTagClick = hasNotes && isAdmin
      ? (e) => onNoteClick(e, marketingListing.listing)
      : undefined;

    return (
      <SrTag
        theme={theme}
        variation='goingToMarketCard'
        onClick={onTagClick}
      >
        {`${pluralize('Note', marketingListing.notesCount, true)}`}
      </SrTag>
    );
  };
  const onCloseNotesModal = () => {
    if (reloadData) {
      reloadData(marketingListing);
    }

    setListingNotesModal(null);
  };

  return (
    <div>
      <SrCard
        noContentPadding
        padding='0 0 25px'
        overflow='hidden'
        margin='0 0'
        isHighlighted={isHighlighted}
      >
        <SrBox padding='0'>
          <SrBox padding='23px 20px 0'>
            <SrFlex justify='between'>
              <SrBox>
                <SrCardOrSrBox
                  target='_blank'
                  to={`/partner/${marketingListing.partner.slug}/listing/${marketingListing.listingId}`}
                >
                  {isAdmin && marketingListing.partner && (
                    <SrText
                      font='greycliff'
                      size='16'
                      color='blue300'
                      display='block'
                    >
                      {marketingListing.partner.displayName}
                    </SrText>
                  )}
                  <SrText
                    font='greycliff'
                    fontSize='21px'
                    weight='800'
                    letterSpacing='0'
                  >
                    {makeListingAddressStreetDisplay(marketingListing.listing)}
                  </SrText>
                  {isAdmin && marketingListing.marketDisplayName && (
                    <SrText
                      font='greycliff'
                      color='grey'
                      size='12'
                      display='block'
                    >
                      {marketingListing.marketDisplayName}
                    </SrText>
                  )}
                </SrCardOrSrBox>
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='500'
                  fontSize={16}
                  display='block'
                  lineHeight='25px'
                >
                  {stateText}
                </SrText>
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  fontSize={14}
                  display='block'
                >
                  Submitted {submittedAt}
                </SrText>
                <SrBox>
                  <SrText
                    font='greycliff'
                    color='green'
                    weight='medium'
                    letterSpacing='0'
                  >
                    {marketingListing.dueDate
                  && `Goes live on or before ${formatPartnerListingDueDate(marketingListing.dueDate)}`
                    }
                    {isAdmin && marketingListing.dueDate && onEditMarketingPartnerListingDueDate && (
                      <SrBox display='inlineBlock' marginLeft='9px'>
                        <SrText
                          color='blue300'
                          font='greycliff'
                          weight='bolder'
                          cursor='pointer'
                          onClick={onEditMarketingPartnerListingDueDate}
                        >
                          Edit
                        </SrText>
                      </SrBox>
                    )}
                  </SrText>
                </SrBox>
                <SrFlex justify='space-between' marginTop='8px'>
                  {getNotesTagComponent()}
                </SrFlex>
              </SrBox>
              <SrLink
                font='greycliff'
                weight='800'
                color='blue300'
                cursor='pointer'
                to={`/partner/${marketingListing.partner.slug}/listing/${marketingListing.listingId}/edit`}
              >
                <SrText
                  font='greycliff'
                  weight='800'
                  color='blue300'
                  cursor='pointer'
                >
                  Edit
                </SrText>
              </SrLink>
            </SrFlex>
          </SrBox>
          <SrSeparator marginTop='20px' marginBottom='10px' />
          <SrBox padding='0 20px'>
            <SrText
              font='greycliff'
              weight='800'
              letterSpacing='0'
            >
              What Sunroom does next:
            </SrText>
            <SrText
              paddingLeft='5px'
              font='greycliff'
              fontSize='16px'
              cursor='pointer'
              decoration='underline'
              color='grey'
              weight='600'
              onClick={() => setCollapseCard(!collapseCard)}
            >
              {
                collapseCard
                  ? 'see more'
                  : 'see less'
              }
            </SrText>
            <SrBox marginTop='13px' />
            {
              !collapseCard && (
                <SrStepsChecklist
                  checkMarkInsteadNumber
                  labelLineThroughOnCompleted
                  steps={checkListSteps}
                />
              )
            }
            {isAdmin && (
              <SrBox marginTop='23px'>
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  hasBoxShadow
                  minWidth='100%'
                  heightType={SR_BUTTON_HEIGHT_MEDIUM}
                  onClick={onEditWalkThroughFields}
                >
                  Report Setup Delay
                </SrButton>
              </SrBox>
            )}
            {marketingListing.partner.experienceConfiguration.additionalMarketingDocsEnabled && (
              <SrBox padding='0 18px' marginTop='23px'>
                <SrFlex justify='center'>
                  <SrText
                    display='block'
                    align='center'
                    fontSize='16px'
                    lineHeight='22px'
                    weight='700'
                    font='greycliff'
                    color={hasDocsForDownload ? 'blue300' : 'grey200'}
                    letterSpacing='0.5px'
                    decoration={hasDocsForDownload ? 'underline' : null}
                    cursor={hasDocsForDownload ? 'pointer' : null}
                    onClick={onDownloadDocsClick}
                  >
                    {
                      hasDocsForDownload
                        ? 'Download Marketing Docs'
                        : 'No Marketing Docs Submitted'
                    }
                  </SrText>
                </SrFlex>
              </SrBox>
            )}
            {downloadDocModalOpen && (
              <SrPreviewAndDownloadDocsModal
                onClose={() => setDownloadDocModalOpen(false)}
                onDownload={onDownloadAdditionalDocs}
                documents={marketingListing.additionalMarketingDocs}
              />
            )}
          </SrBox>
        </SrBox>
      </SrCard>
      {listingNotesModal && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth='546px'
          contentPadding='0px'
          theme={MODAL_THEME_STACK}
          onClose={onCloseNotesModal}
        >
          <SrCard
            header='Notes (Admin-Only)'
            color={SR_CARD_BULLET_COLOR_BLUE}
            maxWidth='none'
          >
            <SrAdminListingNotes
              listingId={listingNotesModal.id}
              dispatch={dispatch}
              currentUser={currentUser}
            />
          </SrCard>
        </SrModalV2>
      )}
      {showEmailSentToPartnerModal && (
        <SrEmailSentToPartnerModal
          onClose={() => setShowEmailSentToPartnerModal(false)}
          onSubmit={onSaveSentToPartner}
        />
      )}
      {showMarkInstallationAsCompletedModal && (
        <SrMarkInstallationAsCompletedModal
          onClose={() => setShowMarkInstallationAsCompletedModal(false)}
          onSubmit={onMarkInstallationAsCompleted}
        />
      )}
    </div>
  );
};

SrGoingToMarketSunRiseListingCard.propTypes = {
  isAdmin: PropTypes.bool,
  reloadData: PropTypes.func,
  isHighlighted: PropTypes.bool,
  marketingListing: PropTypes.object,
  onEditWalkThroughFields: PropTypes.func,
  onEditMarketingPartnerListingDueDate: PropTypes.func,
};

SrGoingToMarketSunRiseListingCard.displayName = 'SrGoingToMarketSunRiseListingCard';

export default SrGoingToMarketSunRiseListingCard;
